<script setup lang="ts">
import { ref } from 'vue'
import type { AuditLog } from '@/types'
import type { Timestamp } from 'firebase/firestore'

const props = defineProps<{ entry: AuditLog }>()

const expanded = ref(false)

// ── Action label map ──────────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  'incident.created': 'Incident created',
  'incident.updated': 'Incident updated',
  'incident.closed': 'Incident closed',
  'subject.created': 'Subject created',
  'subject.updated': 'Subject field updated',
  'subject.photo_uploaded': 'Subject photo uploaded',
  'op.created': 'Operational period created',
  'op.updated': 'Operational period updated',
  'team.created': 'Team created',
  'team.updated': 'Team updated',
  'team.status_changed': 'Team status changed',
  'team.member_added': 'Team member added',
  'team.member_removed': 'Team member removed',
  'assignment.created': 'Assignment created',
  'assignment.updated': 'Assignment updated',
  'assignment.status_changed': 'Assignment status changed',
  'personnel.checked_in': 'Personnel checked in',
  'personnel.checked_out': 'Personnel checked out',
  'personnel.updated': 'Personnel updated',
  'clue.created': 'Clue recorded',
  'clue.status_changed': 'Clue status changed',
  'clue.updated': 'Clue updated',
  'form.updated': 'Form updated',
  'form.exported': 'Form exported as PDF',
  'flyer.generated': 'Missing person flyer generated',
}

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.replace(/[._]/g, ' ')
}

// ── Timestamp ─────────────────────────────────────────────────────────────────

function formatTimestamp(ts: Timestamp | null | undefined): string {
  if (!ts) return '—'
  try {
    return ts.toDate().toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

// ── Entity type badge color ───────────────────────────────────────────────────

const ENTITY_COLORS: Record<string, string> = {
  op: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-blue-300 dark:ring-blue-700',
  team: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 ring-green-300 dark:ring-green-700',
  assignment: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 ring-amber-300 dark:ring-amber-700',
  personnel: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 ring-purple-300 dark:ring-purple-700',
  clue: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 ring-teal-300 dark:ring-teal-700',
  subject: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400 ring-pink-300 dark:ring-pink-700',
  incident: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 ring-indigo-300 dark:ring-indigo-700',
  form: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 ring-yellow-300 dark:ring-yellow-700',
}

function entityColor(type: string): string {
  return ENTITY_COLORS[type] ?? 'bg-surface-container text-outline ring-border'
}

const hasDetail = !!(props.entry.before || props.entry.after)

function formatJson(obj: Record<string, unknown> | null): string {
  if (!obj) return '—'
  return JSON.stringify(obj, null, 2)
}
</script>

<template>
  <div class="border-b border-border last:border-0">
    <!-- Main row -->
    <div
      class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-4 py-3 hover:bg-surface-container-low transition-colors"
      :class="{ 'cursor-pointer': hasDetail }"
      @click="hasDetail && (expanded = !expanded)"
    >
      <!-- Timestamp -->
      <span class="font-label text-xs text-outline shrink-0 w-44">
        {{ formatTimestamp(entry.timestamp as any) }}
      </span>

      <!-- Action description -->
      <span class="flex-1 text-sm text-on-surface">
        {{ actionLabel(entry.action) }}
      </span>

      <!-- Entity badge -->
      <span
        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium ring-1 ring-inset shrink-0"
        :class="entityColor(entry.entity_type)"
      >
        {{ entry.entity_type }}
        <span class="ml-1 opacity-60 hidden xl:inline">
          {{ entry.entity_id.slice(0, 6) }}…
        </span>
      </span>

      <!-- User -->
      <span class="font-mono text-xs text-outline shrink-0 hidden md:block w-36 truncate text-right">
        {{ entry.user_name || '—' }}
      </span>

      <!-- Expand indicator -->
      <span v-if="hasDetail" class="material-symbols-outlined text-[14px] text-outline-variant shrink-0 hidden sm:block">
        {{ expanded ? 'expand_less' : 'expand_more' }}
      </span>
    </div>

    <!-- Before / After detail panel -->
    <div
      v-if="hasDetail && expanded"
      class="px-4 pb-3 bg-surface-container-lowest border-t border-border"
    >
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
        <div v-if="entry.before">
          <p class="font-mono text-xs text-outline uppercase tracking-wide mb-1.5">Before</p>
          <pre class="text-xs font-mono bg-surface border border-border rounded p-2 overflow-x-auto whitespace-pre-wrap text-on-surface-variant">{{ formatJson(entry.before) }}</pre>
        </div>
        <div v-if="entry.after">
          <p class="font-mono text-xs text-outline uppercase tracking-wide mb-1.5">After</p>
          <pre class="text-xs font-mono bg-surface border border-border rounded p-2 overflow-x-auto whitespace-pre-wrap text-on-surface-variant">{{ formatJson(entry.after) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>
